/* ---------------------------------------------------------
 * src/API/creatorTokenUtil.js
 * ---------------------------------------------------------
 *  Creator-role auth storage (100-year persistence)
 *
 *    • Browser / PWA   → js-cookie
 *    • Capacitor       → Preferences / Keychain
 *
 *  Call initCreatorAuthStorage() once at app start.
 * ------------------------------------------------------- */

import { Capacitor }         from '@capacitor/core';
import { Preferences }       from '@capacitor/preferences';
import Cookies               from 'js-cookie';
import { ConfigValue }       from '../Config';
import { clearMemory as clearUserMem } from './token-util';  // ← wipe stale user token

/* ─────────────────────────── Keys ─────────────────────────── */
const KEY          = ConfigValue.AUTH_CREATOR_TOKEN_KEY;   // "blitz-creator-token"
const INFO_KEY     = ConfigValue.AUTH_CREATOR_USER_INFO;

const USER_KEY     = ConfigValue.AUTH_TOKEN_KEY;
const USER_INFO_KEY= ConfigValue.AUTH_USER_INFO;

/* 100-year cookie ≈ 36 500 days */
const PERSIST_DAYS = 365 * 100;

/* ─────────────────── In-memory caches ─────────────────────── */
let tokenCache = null;   // string JWT
let infoCache  = null;   // creator_user blob

/* ─────────────────────── Boot / Hydrate ───────────────────── */
export async function initCreatorAuthStorage() {
  if (Capacitor.isNativePlatform()) {
    const [{ value: tok }, { value: inf }] = await Promise.all([
      Preferences.get({ key: KEY }),
      Preferences.get({ key: INFO_KEY }),
    ]);
    tokenCache = tok || null;
    infoCache  = inf ? JSON.parse(inf) : null;
  } else {
    tokenCache = Cookies.get(KEY)  || null;
    const raw  = Cookies.get(INFO_KEY);
    infoCache  = raw ? JSON.parse(raw) : null;
  }
}

/* ───────────────────── TOKEN helpers ─────────────────────── */
export const getAuthToken = () => tokenCache;

/**
 * Accepts either:
 *   • plain JWT string
 *   • full backend payload { token, creator_user, … }
 */
export async function setAuthToken(tokenObj) {
  const token = typeof tokenObj === 'string' ? tokenObj : tokenObj.token;
  tokenCache  = token;
  infoCache   = typeof tokenObj === 'string' ? null : tokenObj;

  if (Capacitor.isNativePlatform()) {
    await Promise.all([
      Preferences.set({ key: KEY,      value: token }),
      Preferences.set({ key: INFO_KEY, value: JSON.stringify(infoCache) }),
      /* wipe stale user creds in native storage */
      Preferences.remove({ key: USER_KEY }),
      Preferences.remove({ key: USER_INFO_KEY }),
    ]);
  } else {
    Cookies.set(KEY, token,
      { expires: PERSIST_DAYS, secure: true, sameSite: 'Strict' });
    Cookies.set(INFO_KEY, JSON.stringify(infoCache),
      { expires: PERSIST_DAYS, secure: true, sameSite: 'Strict' });

    Cookies.remove(USER_KEY);
    Cookies.remove(USER_INFO_KEY);
  }

  /* also clear user token from *memory* */
  clearUserMem();                      // ← crucial line
}

export async function removeAuthToken() {
  tokenCache = null;
  infoCache  = null;

  if (Capacitor.isNativePlatform()) {
    await Promise.all([
      Preferences.remove({ key: KEY }),
      Preferences.remove({ key: INFO_KEY }),
    ]);
  } else {
    Cookies.remove(KEY);
    Cookies.remove(INFO_KEY);
  }
}

/* ───────────────────── Misc helpers ─────────────────────── */
export const getAuthInfo  = () => infoCache;
export const isAuthorized = () => Boolean(tokenCache);

export const hasBothRoles = () =>
  Boolean(Cookies.get(KEY) && Cookies.get(USER_KEY));

export const activeRole = () => {
  if (Cookies.get(KEY))      return 'creator';
  if (Cookies.get(USER_KEY)) return 'user';
  return null;
};

/* ───────────────── Refresh cookie expiry (optional) ──────── */
export function refreshCookieExpiry() {
  if (!Capacitor.isNativePlatform()) {
    if (tokenCache) {
      Cookies.set(KEY, tokenCache,
        { expires: PERSIST_DAYS, secure: true, sameSite: 'Strict' });
    }
    if (infoCache) {
      Cookies.set(INFO_KEY, JSON.stringify(infoCache),
        { expires: PERSIST_DAYS, secure: true, sameSite: 'Strict' });
    }
  }
}

/* ───────────────── internal helper ───────────────────────── */
/*  Called by token-util to wipe *creator* role from RAM       */
export function clearMemory() {
  tokenCache = null;
  infoCache  = null;
}
